import { Injectable } from '@angular/core'
import { Response, URLSearchParams } from '@angular/http'
import { Store } from "@ngrx/store"
import { AppState } from "@store/app-state"
import { Contact } from "@models/contact"
import { Absence } from "@models/absence"
import { ClassBetween } from "@models/class-between"
import { Observable } from 'rxjs'
import { ActionCableService } from "./action-cable"
import { ADD_CONTACT } from "@reducers/contacts"
import * as moment from "moment"
import { Angular2TokenService } from 'angular2-token';
import { Document } from "@models/document";
import { Note } from "@models/note";
import 'rxjs/add/operator/map'
import 'rxjs/add/operator/catch'

export interface ContactSearchParams {
  name?: string,
  school?: string,
  contact_type?: number,
  gender?: number,
  has_tutor_allocation?: boolean,
  has_debtor_with_cc?: boolean,
}

export interface UniqueEmailCheckResult {
  result: boolean,
  contact?: Contact,
}

export interface ApplyDiscountParams {
  studentId: number,
  discountType: number,
  transactionDate: Date,
  startDate: Date,
  endDate: Date,
  fixedAmount?: number,
}

export interface RemoveDiscountParams {
  discountId: number,
  removeType: number,
}

@Injectable()
export class ContactService {

  private contactsUrl: string = 'contacts'
  private documentsUrl: string = 'documents'
  private notesUrl: string = 'notes'
  private addressesUrl: string = 'addresses'
  private phonesUrl: string = 'phones'
  private absenceUrl: string = 'absences'

  constructor(
    private http: Angular2TokenService,
    private actionCableService: ActionCableService,
    private store: Store<AppState>,
  ) {}

  index(params: ContactSearchParams = {}): Observable<Contact[]> {
    const search: URLSearchParams = new URLSearchParams()
    for (let key in params) { search.set(key, params[key]); }

    return this.http.get(this.contactsUrl, { search })
      .map((res: Response) => {
        return res.json().map((c: any) => Contact.clone(c))
      })
      .catch((error: Response) => Observable.throw(error.json()))
  }

  fetch(id: number): Observable<Contact> {
    return this.http.get(`${this.contactsUrl}/${id}`)
      .map((res: Response) => Contact.clone(res.json()))
      .catch((error: Response) => Observable.throw(error.json()))
  }

  create(params: any): Observable<Contact> {
    return this.http.post(this.contactsUrl, params)
      .map((res: Response) => Contact.clone(res.json()))
      .catch((error: Response) => Observable.throw(error.json()))
  }

  update(params: any): Observable<Contact> {
    return this.http.patch(`${this.contactsUrl}/${params.id}`, params)
      .map((res: Response) => Contact.clone(res.json()))
      .catch((error: Response) => Observable.throw(error.json()))
  }

  classesBetween(p: ContactClassesBetweenParams): Observable<ClassBetween[]> {
    return this.http.post(`${this.contactsUrl}/${p.contactId}/classes_between`, p)
      .map((res: Response) => {
        return res.json().map((r: any): ClassBetween => ClassBetween.clone(r))
      })
      .catch((error: Response) => Observable.throw(error.json()))
  }

  createAbsence(a: Absence): Observable<Absence> {
    return this.http.post(`${this.absenceUrl}`, a.serializeForRails())
      .map((res: Response): Absence => Absence.clone(res.json()))
      .catch((error: Response) => Observable.throw(error.json()))
  }

  deleteAbsence(a: Absence): Observable<Absence> {
    return this.http.delete(`${this.absenceUrl}/${a.id}`)
      .map((): number => a.contactId)
      .catch((error: Response) => Observable.throw(error.json()))
  }

  students(): Observable<Contact[]> {
    return this.http.get(`${this.contactsUrl}/students`)
      .map((res: Response) => { 
        return res.json().map((c: any) => Contact.clone(c))
      })
      .catch((error: Response) => Observable.throw(error.json()))
  }

  checkUniqueEmail(email: string): Observable<UniqueEmailCheckResult> {
    return this.http.put(`${this.contactsUrl}/check_unique_email`, { email })
      .map((res: Response) => {
        const response: any                   =  res.json()
        const result: UniqueEmailCheckResult  = { result: response.result }

        if (response.contact) { result.contact = Contact.clone(response.contact) }

        return result 
      })
      .catch((error: Response) => Observable.throw(error.json()))
  }

  createDocument(document: Document): Observable<Document> {
    return this.http.post(this.documentsUrl, document)
      .map((res: Response) => { return Document.clone(res.json()) })
      .catch((error: Response) => Observable.throw(error.json()))
  }

  deleteDocument(id: number): Observable<number> {
    return this.http.delete(`${this.documentsUrl}/${id}`)
      .map(() => id)
      .catch((error: Response) => Observable.throw(error.json()))
  }

  createNote(note: Note): Observable<Note> {
    return this.http.post(this.notesUrl, note)
      .map((res: Response) => { return Note.clone(res.json()) })
      .catch((error: Response) => Observable.throw(error.json()))
  }

  deleteNote(id: number): Observable<number> {
    return this.http.delete(`${this.notesUrl}/${id}`)
      .map(() => id)
      .catch((error: Response) => Observable.throw(error.json()))
  }

  deleteAddress(id: number): Observable<number> {
    return this.http.delete(`${this.addressesUrl}/${id}`)
      .map(() => id)
      .catch((error: Response) => Observable.throw(error.json()))
  }

  deletePhone(id: number): Observable<number> {
    return this.http.delete(`${this.phonesUrl}/${id}`)
      .map(() => id)
      .catch((error: Response) => Observable.throw(error.json()))
  }

  academicRollover(): Observable<boolean> {
    return this.http.patch(`${this.contactsUrl}/academic_rollover`, {})
      .mapTo(true)
      .catch((error: Response) => Observable.throw(error.json()))
  }

  applyDiscount(params: ApplyDiscountParams): Observable<boolean> {
    return this.http.patch(`${this.contactsUrl}/${params.studentId}/apply_discount`, params)
      .mapTo(true)
      .catch((error: Response) => Observable.throw(error.json()))
  }

  removeDiscount(params: RemoveDiscountParams): Observable<boolean> {
    return this.http.patch(`${this.contactsUrl}/remove_discount`, params)
      .mapTo(true)
      .catch((error: Response) => Observable.throw(error.json()))
  }

  subscribe() {
    this.actionCableService.cable.subscriptions.create(
      { channel: 'ContactsChannel' },
      {
        received: (data: any) => {
          if (data.action === "create") {
            const c: any = data.content
            this.store.dispatch({ type: ADD_CONTACT, payload: Contact.clone(c) })
          }
        },
      }
    )
  }

}

export interface ContactClassesBetweenParams {
  contactId: number,
  startDateTime: string,
  endDateTime: string,
}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/services/contact.ts